/**
 * StorageManager - Orquestador de storage con múltiples backends
 *
 * Centraliza acceso a diferentes sistemas de storage:
 * - IndexedDB: config, metadata, theme (JSON)
 * - OPFS: music (archivos grandes 7MB+)
 * - Cache API: sounds, images (archivos pequeños)
 * - LocalStorage: preferences (strings pequeños)
 *
 * @example
 * await StorageManager.init();
 * await StorageManager.set('config', 'settings', { volume: 80 });
 * const settings = await StorageManager.get('config', 'settings');
 */

import { IndexedDBAdapter } from './adapters/IndexedDBAdapter';
import { OPFSAdapter } from './adapters/OPFSAdapter';
import { CacheAdapter } from './adapters/CacheAdapter';
import { LocalStorageAdapter } from './adapters/LocalStorageAdapter';

// Configuración de qué adapter usar para cada categoría
const STORAGE_DEFAULTS = {
  // JSON/Objects → IndexedDB
  config: 'indexeddb',
  metadata: 'indexeddb',
  theme: 'indexeddb',
  assets: 'indexeddb', // Asset metadata (URLs, versions)
  translations: 'indexeddb', // i18n translations per locale

  // Archivos grandes → OPFS
  music: 'opfs',

  // Archivos pequeños → Cache API
  sounds: 'cache',
  images: 'cache',

  // Preferencias simples → LocalStorage
  preferences: 'localstorage',
};

class StorageManagerClass {
  constructor() {
    this.adapters = new Map();
    this.categoryToAdapter = new Map();
    this.initialized = false;
    this.initPromise = null;

    // Cache en memoria para Howl instances (audio precargado)
    this.audioCache = new Map();
  }

  /**
   * Inicializa todos los adapters
   */
  async init() {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = this._doInit();
    await this.initPromise;
  }

  async _doInit() {
    try {
      // Crear e inicializar adapters
      const indexedDBAdapter = new IndexedDBAdapter('config');
      const opfsAdapter = new OPFSAdapter('audio');
      const cacheAdapter = new CacheAdapter('xhelos-assets');
      const localStorageAdapter = new LocalStorageAdapter('prefs');

      // Inicializar en paralelo
      await Promise.all([
        indexedDBAdapter.init(),
        opfsAdapter.init(),
        cacheAdapter.init(),
        localStorageAdapter.init(),
      ]);

      // Registrar adapters
      this.adapters.set('indexeddb', indexedDBAdapter);
      this.adapters.set('opfs', opfsAdapter);
      this.adapters.set('cache', cacheAdapter);
      this.adapters.set('localstorage', localStorageAdapter);

      // Mapear categorías a adapters
      for (const [category, adapterType] of Object.entries(STORAGE_DEFAULTS)) {
        this.categoryToAdapter.set(category, adapterType);
      }

      this.initialized = true;
      console.log(`[💾 storage] Initialized: ${[...this.adapters.keys()].join(', ')}`);
    } catch (error) {
      console.error('[StorageManager] Failed to initialize:', error);
      throw error;
    }
  }

  /**
   * Verifica que esté inicializado
   */
  ensureInitialized() {
    if (!this.initialized) {
      throw new Error('[StorageManager] Not initialized. Call init() first.');
    }
  }

  /**
   * Obtiene el adapter para una categoría
   * @param {string} category
   * @returns {BaseAdapter}
   */
  getAdapter(category) {
    this.ensureInitialized();

    const adapterType = this.categoryToAdapter.get(category);
    if (!adapterType) {
      throw new Error(`[StorageManager] Unknown category: ${category}`);
    }

    const adapter = this.adapters.get(adapterType);
    if (!adapter) {
      throw new Error(`[StorageManager] Adapter not found: ${adapterType}`);
    }

    return adapter;
  }

  /**
   * Obtiene el tipo de adapter para una categoría
   * @param {string} category
   * @returns {string}
   */
  getAdapterType(category) {
    return this.categoryToAdapter.get(category) || 'indexeddb';
  }

  // ==================== Operaciones Básicas ====================

  /**
   * Verifica si existe un key en una categoría
   */
  async has(category, key) {
    const adapter = this.getAdapter(category);
    return adapter.has(key);
  }

  /**
   * Obtiene un valor
   */
  async get(category, key) {
    const adapter = this.getAdapter(category);
    return adapter.get(key);
  }

  /**
   * Guarda un valor
   */
  async set(category, key, value, options = {}) {
    const adapter = this.getAdapter(category);
    return adapter.set(key, value, options);
  }

  /**
   * Elimina un key
   */
  async delete(category, key) {
    const adapter = this.getAdapter(category);
    return adapter.delete(key);
  }

  /**
   * Limpia una categoría completa
   */
  async clearCategory(category) {
    const adapter = this.getAdapter(category);
    return adapter.clear();
  }

  /**
   * Limpia todo el storage
   */
  async clearAll() {
    this.ensureInitialized();
    const promises = [];
    for (const adapter of this.adapters.values()) {
      promises.push(adapter.clear());
    }
    await Promise.all(promises);
    this.audioCache.clear();
  }

  // ==================== Audio Cache ====================

  /**
   * Guarda un Howl en el cache de memoria
   */
  setAudioCache(category, key, howl) {
    this.audioCache.set(`${category}:${key}`, howl);
  }

  /**
   * Obtiene un Howl del cache de memoria
   */
  getAudioCache(category, key) {
    return this.audioCache.get(`${category}:${key}`) || null;
  }

  /**
   * Verifica si hay un Howl en cache
   */
  hasAudioCache(category, key) {
    return this.audioCache.has(`${category}:${key}`);
  }

  /**
   * Obtiene audio precargado (alias para getAudioCache)
   */
  getPreloadedAudio(category, key) {
    return this.getAudioCache(category, key);
  }

  // ==================== Blob URL helpers ====================

  /**
   * Obtiene un blob URL para un asset
   */
  async getBlobUrl(category, key) {
    const adapter = this.getAdapter(category);

    // OPFS y Cache tienen método getBlobUrl
    if (typeof adapter.getBlobUrl === 'function') {
      return adapter.getBlobUrl(key);
    }

    // Fallback: obtener blob y crear URL
    const blob = await adapter.get(key);
    if (blob instanceof Blob) {
      return URL.createObjectURL(blob);
    }

    return null;
  }

  // ==================== Version validation ====================

  /**
   * Valida si un asset tiene la versión correcta
   */
  async validateVersion(category, key, currentVersion) {
    const adapter = this.getAdapter(category);

    if (typeof adapter.validateVersion === 'function') {
      return adapter.validateVersion(key, currentVersion);
    }

    // Fallback: solo verificar existencia
    const exists = await adapter.has(key);
    return { valid: exists };
  }

  /**
   * Limpia assets con versión incorrecta
   */
  async cleanOutdated(category, currentVersion) {
    const adapter = this.getAdapter(category);

    if (typeof adapter.cleanOutdatedEntries === 'function') {
      return adapter.cleanOutdatedEntries(currentVersion);
    }

    return 0;
  }

  // ==================== Stats ====================

  /**
   * Obtiene estadísticas de todos los adapters
   */
  async getStats() {
    this.ensureInitialized();

    const stats = {};

    for (const [name, adapter] of this.adapters) {
      try {
        stats[name] = await adapter.getStats();
      } catch (error) {
        stats[name] = { error: error.message };
      }
    }

    stats.audioCache = {
      count: this.audioCache.size,
    };

    return stats;
  }
}

// Singleton
export const Storage = new StorageManagerClass();

// Alias for backward compatibility (will be removed)
export const StorageManager = Storage;

export default Storage;
